import Seo from "@/components/Seo";
import { useState } from "react";
import axios from "axios";
import { CORPORATE_ITEMS, IMG } from "@/data/mockData";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Building2, Award, Coins, Gift, Sparkles, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = [Coins, Award, Building2, Gift, Sparkles, Users];

export default function CorporateGifting() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, source: "corporate", subject: "Corporate gifting enquiry" });
      toast("Enquiry received", { description: "Our gifting team will be in touch." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast("Couldn't send", { description: "Please retry in a moment." }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="page-corporate-gifting" className="pt-32 pb-24">
      <Seo path="/corporate-gifting" title="Corporate Silver Gifting" description="Corporate silver gifting from Pune — coins, awards, mementos, festival hampers and employee recognition gifts. Bulk pricing, custom engraving." />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="overline">Corporate gifting</div>
          <h1 className="mt-6 font-serif font-light text-5xl lg:text-7xl leading-[1] tracking-tight">
            Gifts that <span className="italic silver-shimmer">outlast the occasion.</span>
          </h1>
          <p className="mt-8 text-[15px] text-[#2E2E2E]/75 max-w-xl font-light">
            Silver coins, awards, mementos and bespoke gifts — sourced ethically, hallmarked, and packaged to speak for your brand.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CORPORATE_ITEMS.map((c, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div key={c.title} data-testid={`corp-item-${i}`} className="group p-8 rounded-2xl border border-[#EDEDED] bg-white hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.06)] transition-[transform,box-shadow] duration-500">
                <div className="h-11 w-11 rounded-full bg-[#F8F9FA] flex items-center justify-center group-hover:bg-[#1A1A1A] group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" strokeWidth={1.4} />
                </div>
                <h3 className="mt-6 font-serif text-2xl tracking-tight">{c.title}</h3>
                <p className="mt-3 text-sm text-[#2E2E2E]/70 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>

        <section className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 aspect-[4/3] rounded-2xl overflow-hidden">
            <img src={IMG.coin} alt="Corporate silver coins" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-6">
            <div className="overline">Bulk enquiry</div>
            <h2 className="mt-4 font-serif font-light text-4xl leading-tight tracking-tight">Volume orders with white-glove packaging.</h2>
            <form onSubmit={submit} data-testid="corp-form" className="mt-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input required placeholder="Company / Name" data-testid="corp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-lg border-[#E5E5E5]" />
                <Input placeholder="Phone" data-testid="corp-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 rounded-lg border-[#E5E5E5]" />
              </div>
              <Input required type="email" placeholder="Work email" data-testid="corp-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-4 h-11 rounded-lg border-[#E5E5E5]" />
              <Textarea required rows={4} placeholder="Item, quantity, timeline, budget…" data-testid="corp-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-4 rounded-lg border-[#E5E5E5]" />
              <button type="submit" disabled={busy} data-testid="corp-submit" className="btn-fill mt-6 h-12 px-7 rounded-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.2em] hover:text-white disabled:opacity-60">
                {busy ? "Sending…" : "Request a quote"}
              </button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}
